import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, BadgeCheckIcon, CurrencyDollarIcon, UserGroupIcon, CalendarIcon, XIcon, CheckIcon } from '@heroicons/react/outline';
import Modal from '../../common/Modal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import { Switch } from '@headlessui/react';
import toastService from '../../../services/toast';
import { couponsApi } from '../../../services/api/coupons.api';
import { usePermissions } from '../../../contexts/PermissionsContext';

// داتا وهمية للتجربة
const dummyCoupons = [
  {
    id: 1,
    code: "SUMMER25",
    productType: "courses",
    discountType: "percentage",
    discountValue: 25,
    startDate: "2025-01-29",
    endDate: "2025-02-28",
    maxUsers: 100,
    usedCount: 15,
    active: true
  },
  {
    id: 2,
    code: "WELCOME100",
    productType: "books",
    discountType: "fixed",
    discountValue: 100,
    startDate: "2025-02-01",
    endDate: "2025-03-01",
    maxUsers: null,
    usedCount: 0,
    active: true
  },
  {
    id: 3,
    code: "EXPIRED50",
    productType: "courses",
    discountType: "percentage",
    discountValue: 50,
    startDate: "2024-12-01",
    endDate: "2024-12-31",
    maxUsers: 200,
    usedCount: 178,
    active: false
  },
  {
    id: 4,
    code: "BOOKS75",
    productType: "books",
    discountType: "percentage",
    discountValue: 75,
    startDate: "2025-03-01",
    endDate: "2025-04-30",
    maxUsers: 50,
    usedCount: 10,
    active: true
  }
];

const CouponDiscounts = () => {
  const { hasPermission } = usePermissions();
  const [coupons, setCoupons] = useState([]);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const data = await couponsApi.getAllCoupons();
        const formattedCoupons = data.map(coupon => ({
          ...coupon,
          productType: coupon.product_type,
          discountType: coupon.discount_type,
          discountValue: coupon.discount_value,
          startDate: coupon.start_date,
          endDate: coupon.end_date,
          maxUsers: coupon.max_users,
          usedCount: coupon.used_count
        }));
        setCoupons(formattedCoupons);
      } catch (error) {
        toastService.error('حدث خطأ أثناء تحميل الكوبونات');
      }
    };

    fetchCoupons();
  }, []);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    productType: 'courses',
    discountType: 'fixed',
    discountValue: '',
    startDate: '',
    endDate: '',
    maxUsers: '',
    active: true
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar', { 
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // دالة للتحقق من انتهاء الكوبون
  const isExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const handleAdd = () => {
    setFormData({
      code: '',
      productType: 'courses',
      discountType: 'fixed',
      discountValue: '',
      startDate: '',
      endDate: '',
      maxUsers: '',
      active: true
    });
    setIsAddModalOpen(true);
  };

  const handleEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      productType: coupon.productType,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      startDate: coupon.startDate,
      endDate: coupon.endDate,
      maxUsers: coupon.maxUsers || '',
      active: coupon.active
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (coupon) => {
    setSelectedCoupon(coupon);
    setIsDeleteModalOpen(true);
  };

  const validateForm = () => {
    const errors = [];
    
    // Validate coupon code format (alphanumeric, 3-20 chars)
    if (!formData.code) {
      errors.push('يجب إدخال كود الخصم');
    } else if (!/^[A-Za-z0-9]{3,20}$/.test(formData.code)) {
      errors.push('يجب أن يكون الكود من 3-20 حرف، ويحتوي على أحرف وأرقام فقط');
    }

    // Validate discount value
    if (!formData.discountValue) {
      errors.push('يجب إدخال قيمة الخصم');
    } else if (formData.discountValue <= 0) {
      if (formData.discountType === 'percentage') {
        errors.push('يجب أن تكون النسبة بين 1 و 100');
      } else {
        errors.push('يجب أن يكون المبلغ أكبر من 0');
      }
    } else if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      errors.push('يجب أن تكون النسبة بين 1 و 100');
    }

    // Validate dates
    if (!formData.startDate) {
      errors.push('يجب إدخال تاريخ البداية');
    }

    if (!formData.endDate) {
      errors.push('يجب إدخال تاريخ الانتهاء');
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        errors.push('يجب أن يكون تاريخ البداية اليوم أو بعده');
      }

      if (endDate <= startDate) {
        errors.push('يجب أن يكون تاريخ الانتهاء بعد تاريخ البداية');
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
          <ul className="list-disc mr-5">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        code: formData.code,
        product_type: formData.productType,
        discount_type: formData.discountType,
        discount_value: formData.discountValue,
        start_date: formData.startDate,
        end_date: formData.endDate,
        max_users: formData.maxUsers || null,
        active: formData.active
      };

      let responseData;
      if (selectedCoupon) {
        responseData = await couponsApi.updateCoupon(selectedCoupon.id, data);
        const formattedCoupon = {
          ...responseData,
          productType: responseData.product_type,
          discountType: responseData.discount_type,
          discountValue: responseData.discount_value,
          startDate: responseData.start_date,
          endDate: responseData.end_date,
          maxUsers: responseData.max_users,
          usedCount: responseData.used_count
        };
        setCoupons(coupons.map(c => c.id === selectedCoupon.id ? formattedCoupon : c));
        setIsEditModalOpen(false);
        toastService.success('تم تحديث الكوبون بنجاح');
      } else {
        responseData = await couponsApi.addCoupon(data);
        const formattedCoupon = {
          ...responseData,
          productType: responseData.product_type,
          discountType: responseData.discount_type,
          discountValue: responseData.discount_value,
          startDate: responseData.start_date,
          endDate: responseData.end_date,
          maxUsers: responseData.max_users,
          usedCount: responseData.used_count
        };
        setCoupons([...coupons, formattedCoupon]);
        setIsAddModalOpen(false);
        toastService.success('تم إضافة الكوبون بنجاح');
      }

      // إعادة تعيين النموذج
      setFormData({
        code: '',
        productType: 'courses',
        discountType: 'fixed',
        discountValue: '',
        startDate: '',
        endDate: '',
        maxUsers: '',
        active: true
      });
      setSelectedCoupon(null);

    } catch (error) {
      toastService.error('حدث خطأ أثناء حفظ الكوبون');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      // حذف الكوبون
      await couponsApi.deleteCoupon(selectedCoupon.id);
      setCoupons(coupons.filter(c => c.id !== selectedCoupon.id));
      setIsDeleteModalOpen(false);
      toastService.success('تم حذف الكوبون بنجاح');
    } catch (error) {
      // حدث خطأ أثناء حذف الكوبون
      toastService.error('حدث خطأ أثناء حذف الكوبون');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      productType: 'courses',
      discountType: 'fixed',
      discountValue: '',
      startDate: '',
      endDate: '',
      maxUsers: '',
      active: true
    });
  };

  const renderForm = () => (
    <form id="couponForm" onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* كود الخصم */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">كود الخصم</label>
        <input
          type="text"
          value={formData.code}
          onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          className="input-base w-full"
          placeholder="أدخل كود الخصم"
        />
      </div>

      {/* نوع المنتج */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">نوع المنتج</label>
        <select
          value={formData.productType}
          onChange={e => setFormData({ ...formData, productType: e.target.value })}
          className="input-base w-full"
        >
          <option value="courses">دورات</option>
          <option value="books">كتب</option>
        </select>
      </div>

      {/* نوع الخصم وقيمته */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">نوع الخصم</label>
          <select
            value={formData.discountType}
            onChange={e => setFormData({ ...formData, discountType: e.target.value })}
            className="input-base w-full"
          >
            <option value="percentage">نسبة مئوية</option>
            <option value="fixed">مبلغ ثابت</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">قيمة الخصم</label>
          <input
            type="number"
            min="0"
            max={formData.discountType === 'percentage' ? "100" : undefined}
            value={formData.discountValue}
            onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
            className="input-base w-full"
            placeholder={formData.discountType === 'percentage' ? "أدخل النسبة المئوية" : "أدخل المبلغ"}
          />
        </div>
      </div>

      {/* التواريخ */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">تاريخ البداية</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={e => setFormData({ ...formData, startDate: e.target.value })}
            className="input-base w-full"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">تاريخ الانتهاء</label>
          <input
            type="date"
            value={formData.endDate}
            onChange={e => setFormData({ ...formData, endDate: e.target.value })}
            className="input-base w-full"
            min={formData.startDate || new Date().toISOString().split('T')[0]}
          />
        </div>
      </div>

      {/* الحد الأقصى للمستخدمين + السويتش */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">الحد الأقصى للمستخدمين</label>
          <input
            type="number"
            min="0"
            value={formData.maxUsers}
            onChange={e => setFormData({ ...formData, maxUsers: e.target.value })}
            className="input-base w-full"
            placeholder="اتركه فارغاً إذا لم يكن هناك حد أقصى"
          />
        </div>

        <div className="flex items-end mb-[6px]">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.active}
              onChange={checked => setFormData({ ...formData, active: checked })}
              className={`switch-rtl ${
                formData.active ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${
                  formData.active ? '-translate-x-6' : '-translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {formData.active ? 'نشط' : 'غير نشط'}
            </span>
          </div>
        </div>
      </div>
    </form>
  );

  return (
    <div className="p-6">
      {/* بطاقة العنوان */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">كوبونات الخصم</h2>
          <div className="flex items-center gap-4">
            {hasPermission && hasPermission('create_coupon') && (
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={handleAdd}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة كوبون جديد
                </div>
              </div>
            )}
          </div>
        </div>

        {/* محتوى البطاقة */}
        <div className="space-y-4 p-6">
          {coupons.map((coupon) => (
            <div key={coupon.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="p-4">
                {/* رأس البطاقة */}
                <div className="flex items-start gap-6 mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      {coupon.code}
                      <BadgeCheckIcon className={`h-5 w-5 ${
                        isExpired(coupon.endDate)
                          ? 'text-red-500'
                          : !coupon.active 
                            ? 'text-gray-400 dark:text-gray-600'
                            : 'text-green-500'
                      }`} />
                    </h3>
                  </div>
                  <div className="flex items-center gap-3 self-center">
                    {hasPermission && hasPermission('edit_coupon') && (
                      <div className="relative group">
                        <PencilIcon 
                          className="h-5 w-5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer transition-colors duration-200"
                          onClick={() => handleEdit(coupon)}
                        />
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                          تعديل الكوبون
                        </div>
                      </div>
                    )}
                    {hasPermission && hasPermission('delete_coupon') && (
                      <div className="relative group">
                        <TrashIcon 
                          className="h-5 w-5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer transition-colors duration-200"
                          onClick={() => handleDelete(coupon)}
                        />
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                          حذف الكوبون
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* تفاصيل الخصم */}
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-1.5 rounded-md">
                      <CurrencyDollarIcon className="h-4 w-4" />
                      <span>
                        {coupon.discountType === 'percentage' ? `${parseInt(coupon.discountValue)}%` : <>
                          {parseInt(coupon.discountValue)} <span style={{ fontFamily: 'saudi_riyal' }}>&#xE900;</span>
                        </>}
                      </span>
                    </div>
                    {coupon.maxUsers && (
                      <div className="flex items-center gap-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2.5 py-1.5 rounded-md">
                        <UserGroupIcon className="h-4 w-4" />
                        <span>{coupon.usedCount || 0}/{coupon.maxUsers}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon className="h-4 w-4" />
                      <span>من: {formatDate(coupon.startDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon className="h-4 w-4" />
                      <span>إلى: {formatDate(coupon.endDate)}</span>
                    </div>
                  </div>

                  {/* نوع المنتج */}
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 px-2.5 py-1.5 rounded-md">
                    <span>
                      {coupon.productType === 'courses' ? 'دورات' : 'كتب'}
                    </span>
                  </div>

                  {/* شريط التقدم للاستخدام */}
                  {coupon.maxUsers && (
                    <div className="flex-1 flex items-center gap-4">
                      <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        نسبة الاستخدام: {Math.min(100, parseInt(((coupon.usedCount || 0) / coupon.maxUsers) * 100))}%
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 dark:bg-blue-500 rounded-full h-2 transition-all duration-300"
                          style={{ width: `${Math.min(100, parseInt(((coupon.usedCount || 0) / coupon.maxUsers) * 100))}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* مودال الإضافة */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsAddModalOpen(false);
            setSelectedCoupon(null);
            resetForm();
          }
        }}
        title="إضافة كوبون جديد"
        actions={
          <button
            type="submit"
            form="couponForm"
            disabled={isLoading}
            className="relative group"
          >
            <CheckIcon 
              className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
            />
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
              إضافة
            </div>
          </button>
        }
      >
        {renderForm()}
      </Modal>

      {/* مودال التعديل */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsEditModalOpen(false);
            setSelectedCoupon(null);
            resetForm();
          }
        }}
        title="تعديل كوبون خصم"
        actions={
          <button
            type="submit"
            form="couponForm"
            disabled={isLoading}
            className="relative group"
          >
            <CheckIcon 
              className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
            />
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
              تحديث
            </div>
          </button>
        }
      >
        {renderForm()}
      </Modal>

      {/* مودال الحذف */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsDeleteModalOpen(false);
            setSelectedCoupon(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        itemType="الكوبون"
        itemTitle={selectedCoupon?.code}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CouponDiscounts;